'use client'

import { twMerge } from 'tailwind-merge'
import _ from 'lodash'
import { useEffect, useState, useRef } from 'react'
import { useDrop } from 'ahooks'
import { ReactSortable } from 'react-sortablejs'
import { acceptImageTypes } from '@/settings'
import { Button } from '@/components'
import { PlusIcon } from '@heroicons/react/20/solid'

type Props = {
  id?: string
  'data-error'?: string | any
  onChange: (files: File[]) => void
  isSaved?: boolean
}

type MyFile = File & {
  id: string
  selected?: boolean
  preview?: string
}

export function PhotoUpload(props: Props) {
  const { onChange, isSaved } = props
  const errMsg = _.get(props, 'data-error')

  useEffect(() => {
    if (isSaved) {
      setPhotos([])
    }
  }, [isSaved])

  const [photos, setPhotos] = useState<MyFile[]>([])
  useEffect(() => {
    onChange(photos)
  }, [photos])

  // select file
  const fileInputRef = useRef<any>(null)
  const selectFile = () => { fileInputRef.current.click() }
  const selectFileChanged = (e: any) => {
    if (e.target.files.length > 0) {
      const d = _.filter(e.target.files, x => acceptImageTypes.includes(x.type))
      _.forEach(d, x => {
        x.id = _.uniqueId()
        x.preview = window.URL.createObjectURL(x)
      })
      setPhotos([...photos, ...d])
    }
  }

  // drop file
  const [isHovering, setIsHovering] = useState(false)
  const dropRef = useRef(null)
  useDrop(dropRef, {
    onFiles: (files) => {
      const d = files.filter(x => acceptImageTypes.includes(x.type)) as MyFile[]
      _.forEach(d, x => {
        x.id = _.uniqueId()
        x.preview = window.URL.createObjectURL(x)
      })
      setPhotos([...photos, ...d])
      setIsHovering(false)
    },
    // onUri: (uri) => {
    //   setIsHovering(false)
    // },
    onDragEnter: () => setIsHovering(true),
    onDragLeave: () => setIsHovering(false),
  })

  // selected photos
  const [countSelected, setCountSelected] = useState(0)
  function toggleSelectPhoto(id: string) {
    const file = _.find(photos, { id })
    if (!file) return
    file.selected = !file.selected
    setPhotos([...photos])
    setCountSelected(_.filter(photos, 'selected').length)
  }

  function deleteSelectedPhotos() {
    setPhotos(_.filter(photos, x => !x.selected))
    setCountSelected(0)
  }

  function unSelectPhotos() {
    setPhotos(_.map(photos, x => ({ ...x, selected: false })))
    setCountSelected(0)
  }

  return (
    <div ref={dropRef}
      className={
        twMerge(
          'dropzone',
          isHovering && 'shadow-lg border-zinc-400 dark:border-zinc-500',
          errMsg && 'ring-1 ring-red-800'
        )
      }
    >
      <div className='text-center font-normal text-base/8 py-3'>
        <input type='file' multiple ref={fileInputRef} className='hidden'
          accept={acceptImageTypes.join(',')}
          onChange={selectFileChanged}
        />
        To upload new photo(s),
        please <Button type='button' className='h-8 rounded-full px-4' onClick={selectFile}><PlusIcon className='w-4 mr-0.5 -ml-1' />Select</Button> or <span className='font-semibold'>drag
          & drop</span> your photo(s) here
        {
          errMsg && <div className='form-error-message mt-4 text-base/6!'>{errMsg}</div>
        }
      </div>
      <ReactSortable
        animation={400}
        list={photos}
        setList={setPhotos}
        className='w-full grid grid-cols-4 md:grid-cols-3 xl:grid-cols-4 items-center gap-2'
      >
        {
          _.map(photos, (x) => {
            return (
              <div key={x.id} onClick={() => toggleSelectPhoto(x.id)}
                className={twMerge('border py-1 rounded-md', x.selected && 'bg-red-800')}>
                <img src={x?.preview} className='aspect-16/10 object-contain m-auto' />
              </div>
            )
          })
        }
      </ReactSortable>
      {
        countSelected > 0 &&
        <div className='flex items-center gap-x-2 mt-4'>
          <Button type='button' variant='outline' onClick={unSelectPhotos} className='w-24 h-8 rounded-full'>
            Cancel
          </Button>
          <Button type='button' variant='secondary' onClick={deleteSelectedPhotos} className='w-24 h-8 rounded-full'>
            Remove
          </Button>
        </div>
      }
    </div>
  )
}
